import type { BodyPoseCapabilities } from '../services/bodyPose';

export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
export const VIDEO_ACCEPT = '.mp4,.mov,.m4v,.webm,.mkv,.avi';
export const BASIC_BODY_POSE_CAPABILITIES: BodyPoseCapabilities = {
  premium: false, max_upload_bytes: MAX_VIDEO_BYTES, max_duration_seconds: 60,
  segment_max_bytes: MAX_VIDEO_BYTES, segment_max_seconds: 60, max_segments: 1,
  automatic_resolution: true,
};

export interface VideoMetadata { duration: number; width: number; height: number; }

export function validateVideoFile(file: File, limits = BASIC_BODY_POSE_CAPABILITIES): string | null {
  if (!/\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(file.name)) {
    return 'Choose an MP4, MOV, M4V, WebM, MKV or AVI video.';
  }
  if (!file.size) return 'This file is empty. Choose a video with content.';
  if (file.size > limits.max_upload_bytes) {
    return `The video must be no larger than ${limits.max_upload_bytes / (1024 * 1024)} MiB.${limits.premium ? '' : ' Premium supports larger videos with automatic splitting.'}`;
  }
  return null;
}

export function validateVideoMetadata({ duration, width, height }: VideoMetadata, limits = BASIC_BODY_POSE_CAPABILITIES): string | null {
  if (!Number.isFinite(duration) || duration < 1 || duration > limits.max_duration_seconds + 0.001) {
    const premiumHint = !limits.premium && Number.isFinite(duration) && duration > limits.max_duration_seconds
      ? ' Premium supports longer videos with automatic splitting.' : '';
    return `Choose a video from 1 to ${limits.max_duration_seconds} seconds.${premiumHint}`;
  }
  if (!Number.isFinite(width) || !Number.isFinite(height) || Math.min(width, height) < 256 || Math.max(width, height) > 4096 || width * height > 8400000) {
    return 'Each video dimension must be between 256 and 4096 pixels, up to 8.4 megapixels.';
  }
  // The worker adjusts resolution to the processing budget while preserving aspect ratio.
  return null;
}

export function newSubmissionKey(): string {
  // getRandomValues is also available on local HTTP development origins.
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('');
}
